import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Typography,
  Box,
  Card,
  CardContent,
  Radio,
  Chip,
  Tooltip,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Info, CheckCircle, Close } from '@mui/icons-material';
import { ApiError } from '../../utils/error-handler';
import { useOrganization } from '../../context/OrganizationContext';
import { Subscription } from '../../types';

interface CreateTeamFormData {
  name: string;
  description: string;
  subscriptionId: string;
}

interface CreateTeamDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateTeamFormData) => Promise<{ success: boolean; error?: ApiError }>;
  organizationId: string;
}

const CreateTeamDialog: React.FC<CreateTeamDialogProps> = ({
  open,
  onClose,
  onSubmit,
  organizationId
}) => {
  const { subscriptions, getOrganizationSubscriptions } = useOrganization();

  // Filter subscriptions for the current organization and only active/trialing ones with capacity
  const availableSubscriptions = React.useMemo(() => {
    if (!organizationId) return [];

    const orgSubs = getOrganizationSubscriptions(organizationId);

    // Filter to only show active subscriptions with available capacity
    return orgSubs.filter(sub => {
      // Must have active availability (not expired/unusable)
      if (sub.availability === 'inactive') return false;

      // Must be active or trial (lowercase as per API)
      if (sub.status !== 'active' && sub.status !== 'trial') return false;

      // Check team capacity
      const teamsLimit = sub.limits?.teamsPerOrg ?? sub.limits?.maxTeams ?? 0;
      const currentTeamsCount = sub.currentUsage?.teamsUsed ?? sub.currentUsage?.teamsCount ?? sub.assignedTeamCount ?? 0;
      const hasTeamCapacity = teamsLimit === 0 || teamsLimit > currentTeamsCount;

      // Check storage capacity (in bytes)
      const storageLimit = sub.limits?.maxStorageBytes ?? 0;
      const storageUsed = sub.currentUsage?.storageUsedBytes ?? 0;
      const hasStorageCapacity = storageLimit === 0 || storageUsed < storageLimit;

      return hasTeamCapacity && hasStorageCapacity;
    });
  }, [organizationId, getOrganizationSubscriptions, subscriptions]);

  const [formData, setFormData] = useState<CreateTeamFormData>({
    name: '',
    description: '',
    subscriptionId: ''
  });
  const [errors, setErrors] = useState<Partial<Record<keyof CreateTeamFormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<ApiError | null>(null);

  // Set default subscription when dialog opens or subscriptions change
  useEffect(() => {
    if (open && availableSubscriptions.length > 0 && !formData.subscriptionId) {
      setFormData(prev => ({ ...prev, subscriptionId: availableSubscriptions[0]._id }));
    }
  }, [open, availableSubscriptions]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setFormData({ name: '', description: '', subscriptionId: '' });
      setErrors({});
      setApiError(null);
    }
  }, [open]);

  const handleChange = (field: keyof CreateTeamFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error when field is edited
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }

    // Clear API error when any field is edited
    if (apiError) {
      setApiError(null);
    }
  };

  const handleSubscriptionChange = (subscriptionId: string) => {
    setFormData(prev => ({ ...prev, subscriptionId }));

    // Clear error when field is edited
    if (errors.subscriptionId) {
      setErrors(prev => ({ ...prev, subscriptionId: undefined }));
    }

    // Clear API error when any field is edited
    if (apiError) {
      setApiError(null);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CreateTeamFormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Team name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Team name must be at least 2 characters';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Team name cannot exceed 100 characters';
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description cannot exceed 500 characters';
    }

    if (!formData.subscriptionId || formData.subscriptionId.trim() === '') {
      newErrors.subscriptionId = 'Subscription is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setApiError(null);

    try {
      const result = await onSubmit(formData);

      if (result.success) {
        // Form reset happens in useEffect when dialog closes
        onClose();
      } else if (result.error) {
        setApiError(result.error);
      }
    } catch (error) {
      console.error('Error creating team:', error);
      setApiError({
        message: 'An unexpected error occurred',
        code: 'UNKNOWN_ERROR',
        status: 500
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to get subscription usage status label and color
  const getUsageStatus = (sub: Subscription) => {
    const teamsUsed = sub.currentUsage?.teamsUsed ?? sub.currentUsage?.teamsCount ?? sub.assignedTeamCount ?? 0;
    const teamsLimit = sub.limits?.teamsPerOrg ?? sub.limits?.maxTeams ?? 0;

    if (teamsLimit === 0) {
      return { label: 'No team limit set', color: 'info' as const };
    }

    const percentUsed = (teamsUsed / teamsLimit) * 100;

    if (percentUsed >= 100) return { label: 'At capacity', color: 'error' as const };
    if (percentUsed >= 80) return { label: `${teamsUsed}/${teamsLimit} teams`, color: 'warning' as const };
    return { label: `${teamsUsed}/${teamsLimit} teams`, color: 'success' as const };
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '14px',
          boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 16px 40px rgba(15,23,42,.10)',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: 20,
          fontWeight: 800,
          letterSpacing: '-0.02em',
          paddingBottom: 0.5,
        }}
      >
        Create a new team
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          disabled={isSubmitting}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography
          sx={(theme) => ({
            fontSize: 12.5,
            color: theme.palette.text.secondary,
            marginBottom: 2,
          })}
        >
          Teams are workspaces inside your organization. You'll be the first TeamAdmin.
        </Typography>

        {apiError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {apiError.message}
          </Alert>
        )}

        {availableSubscriptions.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This organization does not have any active subscriptions with available capacity.
            Please add a subscription or upgrade your current plan before creating a team.
          </Alert>
        )}

        <TextField
          margin="dense"
          required
          fullWidth
          size="small"
          id="name"
          label="Team Name"
          name="name"
          autoFocus
          value={formData.name}
          onChange={handleChange('name')}
          error={!!errors.name}
          helperText={errors.name || 'Choose a name that reflects the team\'s purpose'}
          disabled={isSubmitting}
        />

        <TextField
          margin="dense"
          fullWidth
          size="small"
          id="description"
          label="Description"
          name="description"
          multiline
          rows={3}
          value={formData.description}
          onChange={handleChange('description')}
          error={!!errors.description}
          helperText={errors.description || 'Optional: Describe the purpose and focus of this team'}
          disabled={isSubmitting}
          sx={{ mt: 2 }}
        />

        {/* Subscription Selection */}
        <Box sx={{ mt: 3 }}>
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', fontWeight: 700 }}
          >
            Assign Subscription *
            <Tooltip title="Teams must be assigned to a subscription plan to manage resource usage limits">
              <Info fontSize="small" sx={{ ml: 1, color: 'info.main' }} />
            </Tooltip>
          </Typography>

          {availableSubscriptions.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              {availableSubscriptions.map((sub) => {
                const usageStatus = getUsageStatus(sub);

                return (
                  <Card
                    key={sub._id}
                    variant="outlined"
                    sx={{
                      mb: 1.5,
                      borderColor: formData.subscriptionId === sub._id ? 'primary.main' : 'divider',
                      borderWidth: formData.subscriptionId === sub._id ? 2 : 1,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: 'primary.light',
                        boxShadow: 1
                      }
                    }}
                    onClick={() => !isSubmitting && handleSubscriptionChange(sub._id)}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Radio
                          checked={formData.subscriptionId === sub._id}
                          value={sub._id}
                          sx={{ mt: -0.5 }}
                          disabled={isSubmitting}
                          size="small"
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                              <Typography variant="subtitle2" component="div" sx={{ wordBreak: 'break-word', fontWeight: 600 }}>
                                {sub.name}
                              </Typography>
                              {formData.subscriptionId === sub._id && (
                                <CheckCircle color="primary" fontSize="small" />
                              )}
                            </Box>
                            <Chip
                              size="small"
                              label={usageStatus.label}
                              color={usageStatus.color}
                              sx={{ flexShrink: 0 }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {sub.plan ? (sub.plan.charAt(0).toUpperCase() + sub.plan.slice(1)) : 'Standard'} Plan
                            {' • '}
                            {sub.currentUsage?.activeUsers ?? sub.currentUsage?.currentConcurrentUsers ?? 0} active users
                            {' • '}
                            {((sub.currentUsage?.storageUsedBytes ?? 0) / (1024 * 1024 * 1024)).toFixed(2)}GB storage used
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
              {errors.subscriptionId && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                  {errors.subscriptionId}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Select a subscription plan to assign to this team. The team will share resources with other teams on the same plan.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: '12px 24px 16px' }}>
        <Button onClick={onClose} variant="outlined" size="small" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          size="small"
          color="primary"
          disabled={!formData.name.trim() || !formData.subscriptionId || isSubmitting || availableSubscriptions.length === 0}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
        >
          {isSubmitting ? 'Creating...' : 'Create Team'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTeamDialog;
